
import ExcelJS from 'exceljs';
import {
  RuntimeSurveyQuestion,
  SurveyResponseDetail,
  getSurveyQuestionsBySurveyId,
  getSurveyResponseByCustomerIdentity,
} from '../Survey/surveyApi';

const DEFAULT_STATUS = 'Open';

function getAnswerText(detail: SurveyResponseDetail, question: RuntimeSurveyQuestion): string {
  const selectedOptions = detail.surveyResponseDetailsOptionsDTOList ?? [];

  if (selectedOptions.length > 0) {
    const selectedIds = new Set(selectedOptions.map((o) => {return o.questionOptionId}));
    const titles = question.options
      .filter((o) => {return selectedIds.has(o.questionOptionId)})
      .map((o) => {return o.optionTitle});

    if (titles.length > 0) {
      return titles.join(', ');
    }
  }

  return detail.response ?? '';
}

// matches legacy `formatCurrencyValue` in survey-export.ts / customer-preview-table.component.ts
function isCurrencyQuestion(question: RuntimeSurveyQuestion): boolean {
  return (question.questionType ?? '').trim().toLowerCase() === 'currency';
}

function formatCurrencyValue(value: string): string {
  if (!value) {
    return value;
  }

  const numericValue = Number(value.replace(/,/g, ''));
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return '$' + numericValue.toLocaleString('en-US', { maximumFractionDigits: 20 });
}

export async function downloadSurveyExcel(args: {
  customerNumber: string;
  customerDetails: {
    employerGroupEIN: string | null;
    fundingType: string;
    reportingYear: number;
    policyList: string;
  };
  surveyId: number;
  employerGroupName: string;
  surveyYear: string;
}): Promise<void> {
  const [questions, surveyResponse] = await Promise.all([
    getSurveyQuestionsBySurveyId({ surveyId: args.surveyId }),
    getSurveyResponseByCustomerIdentity({
      customerNumber: args.customerNumber,
      fundingType: args.customerDetails.fundingType,
      reportingYear: args.customerDetails.reportingYear,
      surveyId: args.surveyId,
    }),
  ]);

  if (!surveyResponse) {
    throw new Error('No survey response found for this customer.');
  }

  const questionMap = new Map<number, RuntimeSurveyQuestion>(
    questions.map((q) => {return [q.questionId, q]}),
  );

  const sortedDetails = [...surveyResponse.surveyResponseDetailsDTOList].sort((a, b) => {
    const qa = questionMap.get(a.questionId);
    const qb = questionMap.get(b.questionId);
    return (qa?.orderId ?? 0) - (qb?.orderId ?? 0);
  });

  const dataRows: [string, string][] = [];

  for (const detail of sortedDetails) {
    const question = questionMap.get(detail.questionId);

    if (!question) {
      continue;
    }

    const answer = getAnswerText(detail, question);

    if (!answer.trim()) {
      continue;
    }

    dataRows.push([
      question.questionTitle,
      isCurrencyQuestion(question) ? formatCurrencyValue(answer) : answer,
    ]);
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('RFI Responses');

  worksheet.getColumn(1).width = 60;
  worksheet.getColumn(2).width = 80;
  worksheet.addRow(['Employer Group Name', args.employerGroupName]);
  worksheet.addRow(['EIN', args.customerDetails.employerGroupEIN ?? '']);
  worksheet.addRow(['Funding Type', args.customerDetails.fundingType]);
  worksheet.addRow(['Reporting year', args.customerDetails.reportingYear]);
  worksheet.addRow(['Policy List', args.customerDetails.policyList]);
  worksheet.addRow(['RFI Status', surveyResponse.status ?? DEFAULT_STATUS]);
  dataRows.forEach((row) => {worksheet.addRow(row)});

  const safeGroupName = args.employerGroupName.replace(/[/\\?*[\]:]/g, '_');
  const filename = `${args.surveyYear} CAA RxDC RFI - ${safeGroupName}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}
